import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getGlobalRate,
  getHistory,
  getItemOverrides,
  getPartnerOverrides,
} from '../../services/commissionsService'
import ChangeGlobalRateModal from './components/ChangeGlobalRateModal'
import SetOverrideModal, { OverrideTarget } from './components/SetOverrideModal'


type TabKey = 'dashboard' | 'partners' | 'items'


const SCOPE_LABEL: Record<string, string> = {
  global: 'Global',
  partner: 'Partner',
  experience: 'Experience',
  engagement: 'Engagement',
}


const formatRate = (v: any) => (v == null ? '—' : `${Number(v).toFixed(2)}%`)

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })

const initials = (str: string) => {
  const words = (str || '').trim().split(/\s+/)
  if (words.length === 0) return '·'
  const first = words[0][0] || ''
  const last = words.length > 1 ? words[words.length - 1][0] : ''
  return `${first}${last}`.toUpperCase() || (str[0] || '·').toUpperCase()
}


export const CommissionsAdminPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dashboard')

  const [global, setGlobal] = useState<any>(null)
  const [history, setHistory] = useState<any[]>([])
  const [showGlobalModal, setShowGlobalModal] = useState(false)

  const [partnerRows, setPartnerRows] = useState<any[]>([])
  const [partnersHasOverride, setPartnersHasOverride] = useState('all')
  const [partnersSearch, setPartnersSearch] = useState('')

  const [itemRows, setItemRows] = useState<any[]>([])
  const [itemType, setItemType] = useState<'experience' | 'engagement'>('experience')
  const [itemsHasOverride, setItemsHasOverride] = useState('all')
  const [itemsSearch, setItemsSearch] = useState('')

  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null)

  const refreshDashboard = useCallback(async () => {
    const [g, h, p, i] = await Promise.all([
      getGlobalRate(),
      getHistory({ limit: 15, offset: 0 }),
      getPartnerOverrides({ hasOverride: 'true' }),
      getItemOverrides({ itemType: 'experience', hasOverride: 'true' }),
    ])
    setGlobal(g)
    setHistory(h.results ?? [])
    setPartnerRows(Array.isArray(p) ? p : p.results ?? [])
    setItemRows(Array.isArray(i) ? i : i.results ?? [])
  }, [])

  const refreshPartners = useCallback(async () => {
    const data = await getPartnerOverrides({
      hasOverride: partnersHasOverride,
      search: partnersSearch,
    })
    setPartnerRows(Array.isArray(data) ? data : data.results ?? [])
  }, [partnersHasOverride, partnersSearch])

  const refreshItems = useCallback(async () => {
    const data = await getItemOverrides({
      itemType,
      hasOverride: itemsHasOverride,
      search: itemsSearch,
    })
    setItemRows(Array.isArray(data) ? data : data.results ?? [])
  }, [itemType, itemsHasOverride, itemsSearch])

  useEffect(() => {
    if (tab === 'dashboard') refreshDashboard()
    if (tab === 'partners') refreshPartners()
    if (tab === 'items') refreshItems()
  }, [tab, refreshDashboard, refreshPartners, refreshItems])

  const openPartnerOverride = (row: any) =>
    setOverrideTarget({
      scope: 'partner',
      targetId: row.id,
      label: row.displayName,
      currentOverride: row.commissionRate != null ? String(row.commissionRate) : null,
    })

  const openItemOverride = (row: any) =>
    setOverrideTarget({
      scope: row.itemType === 'engagement' ? 'engagement' : 'experience',
      targetId: row.id,
      label: row.title,
      currentOverride: row.commissionRate != null ? String(row.commissionRate) : null,
    })

  const handleOverrideSaved = async () => {
    setOverrideTarget(null)
    if (tab === 'dashboard') await refreshDashboard()
    if (tab === 'partners') await refreshPartners()
    if (tab === 'items') await refreshItems()
  }

  const partnerOverrideCount = useMemo(
    () => partnerRows.filter((r) => r.commissionRate != null).length,
    [partnerRows]
  )
  const itemOverrideCount = useMemo(
    () => itemRows.filter((r) => r.commissionRate != null).length,
    [itemRows]
  )

  return (
    <div className='nb-commissions'>
      {/* HEADER */}
      <div className='nb-commissions__header'>
        <div className='nb-commissions__title-block'>
          <h1 className='nb-commissions__title'>Commission Rates</h1>
          <div className='nb-commissions__subtitle'>
            The percentage Nobilis retains from every partner-facing charge.
          </div>
        </div>
        <div className='nb-commissions__hierarchy-chip'>
          Resolver&nbsp;·&nbsp;<strong>Item</strong>&nbsp;→&nbsp;<strong>Partner</strong>&nbsp;→&nbsp;<strong>Global</strong>
        </div>
      </div>

      {/* TABS */}
      <div className='nb-commissions__tabs'>
        {(['dashboard', 'partners', 'items'] as TabKey[]).map((t) => (
          <button
            key={t}
            type='button'
            className={`nb-commissions__tab ${tab === t ? 'nb-commissions__tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'dashboard'
              ? 'Dashboard'
              : t === 'partners'
              ? 'Partner overrides'
              : 'Item overrides'}
          </button>
        ))}
      </div>

      {/* DASHBOARD TAB */}
      {tab === 'dashboard' && (
        <>
          <div className='nb-commissions__grid'>
            {/* Global rate — hero card */}
            <div className='nb-commissions__card'>
              <div className='nb-commissions__card-label'>Global default rate</div>
              <div className='nb-commissions__hero-value'>
                <span className='nb-commissions__hero-number'>
                  {global?.defaultRate != null ? Number(global.defaultRate).toFixed(1) : '—'}
                </span>
                <span className='nb-commissions__hero-unit'>%</span>
              </div>
              <div className='nb-commissions__hero-rule' />
              <div className='nb-commissions__card-caption'>
                Applies when no item-level or partner-level override exists. Changes
                affect future charges only — historical payouts stay frozen.
              </div>
              <div className='nb-commissions__card-footer'>
                <span>
                  {global?.lastChange
                    ? `Updated by ${global.lastChange.actorName || global.lastChange.actorEmail} · ${formatDate(global.lastChange.createdAt)}`
                    : 'No changes recorded yet'}
                </span>
                <button
                  type='button'
                  className='nb-commissions__btn nb-commissions__btn--primary nb-commissions__btn--sm'
                  onClick={() => setShowGlobalModal(true)}
                >
                  Change rate
                </button>
              </div>
            </div>

            {/* Partner overrides count */}
            <div className='nb-commissions__card'>
              <div className='nb-commissions__card-label'>Partner overrides</div>
              <div className='nb-commissions__stat-number'>{partnerOverrideCount}</div>
              <div className='nb-commissions__stat-suffix'>Active</div>
              <div className='nb-commissions__card-caption'>
                Partners with a custom rate that beats the global default across
                all their actions.
              </div>
              <div className='nb-commissions__card-footer'>
                <span />
                <button
                  type='button'
                  className='nb-commissions__link'
                  onClick={() => setTab('partners')}
                >
                  Manage →
                </button>
              </div>
            </div>

            {/* Item overrides count */}
            <div className='nb-commissions__card'>
              <div className='nb-commissions__card-label'>Item overrides</div>
              <div className='nb-commissions__stat-number'>{itemOverrideCount}</div>
              <div className='nb-commissions__stat-suffix'>Active</div>
              <div className='nb-commissions__card-caption'>
                Experiences or engagements with a custom rate that beats both the
                partner and the global default.
              </div>
              <div className='nb-commissions__card-footer'>
                <span />
                <button
                  type='button'
                  className='nb-commissions__link'
                  onClick={() => setTab('items')}
                >
                  Manage →
                </button>
              </div>
            </div>
          </div>

          {/* Recent changes */}
          <div className='nb-commissions__section'>
            <div className='nb-commissions__section-header'>
              <h2 className='nb-commissions__section-title'>Recent changes</h2>
              <div className='nb-commissions__section-hint'>
                Append-only audit log. Every change writes one row; edits and deletes are not allowed.
              </div>
            </div>
            <div className='nb-commissions__table-wrap'>
              <table className='nb-commissions__table'>
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>When</th>
                    <th style={{ width: 120 }}>Scope</th>
                    <th>Target</th>
                    <th style={{ width: 160 }}>Change</th>
                    <th>Reason</th>
                    <th style={{ width: 200 }}>By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr>
                      <td colSpan={6} className='nb-commissions__table__empty'>
                        No changes yet. Every rate change writes an entry here.
                      </td>
                    </tr>
                  )}
                  {history.map((row) => (
                    <tr key={row.id}>
                      <td style={{ color: '#808080', fontSize: 12 }}>
                        {formatDate(row.createdAt)}
                      </td>
                      <td>
                        <span
                          className={`nb-commissions__scope nb-commissions__scope--${row.scope}`}
                        >
                          {SCOPE_LABEL[row.scope] ?? row.scope}
                        </span>
                      </td>
                      <td style={{ color: row.targetId ? '#151515' : '#808080' }}>
                        {row.targetId ? `${row.targetType} #${row.targetId}` : '—'}
                      </td>
                      <td>
                        <span className='nb-commissions__delta'>
                          <span className='nb-commissions__delta-old'>{formatRate(row.oldValue)}</span>
                          <span className='nb-commissions__delta-arrow'>→</span>
                          <span className='nb-commissions__delta-new'>{formatRate(row.newValue)}</span>
                        </span>
                      </td>
                      <td>
                        <div className='nb-commissions__reason' title={row.reason}>
                          {row.reason}
                        </div>
                      </td>
                      <td>
                        <span className='nb-commissions__party'>
                          <span className='nb-commissions__avatar'>
                            {initials(row.actorName || row.actorEmail || '')}
                          </span>
                          <span className='nb-commissions__party-name'>
                            {row.actorName || row.actorEmail}
                          </span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* PARTNERS TAB */}
      {tab === 'partners' && (
        <>
          <div className='nb-commissions__filter-bar'>
            <div className='nb-commissions__filter'>
              <span className='nb-commissions__filter-label'>Search</span>
              <input
                className='nb-commissions__filter-input'
                type='text'
                placeholder='Company name…'
                value={partnersSearch}
                onChange={(e) => setPartnersSearch(e.target.value)}
              />
            </div>
            <div className='nb-commissions__filter'>
              <span className='nb-commissions__filter-label'>Override</span>
              <select
                className='nb-commissions__filter-select'
                value={partnersHasOverride}
                onChange={(e) => setPartnersHasOverride(e.target.value)}
              >
                <option value='all'>All partners</option>
                <option value='true'>With override</option>
                <option value='false'>Without override</option>
              </select>
            </div>
            <div className='nb-commissions__filter-spacer' />
          </div>

          <div className='nb-commissions__table-wrap'>
            <table className='nb-commissions__table'>
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Email</th>
                  <th className='nb-commissions__table--right' style={{ width: 140 }}>Effective</th>
                  <th className='nb-commissions__table--right' style={{ width: 140 }}>Override</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {partnerRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className='nb-commissions__table__empty'>
                      No partners match the current filters.
                    </td>
                  </tr>
                )}
                {partnerRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className='nb-commissions__party'>
                        <span className='nb-commissions__avatar'>{initials(row.displayName)}</span>
                        <span className='nb-commissions__party-name'>{row.displayName}</span>
                      </span>
                    </td>
                    <td style={{ color: '#808080' }}>{row.email}</td>
                    <td className='nb-commissions__table--right'>
                      <span className='nb-commissions__rate'>{formatRate(row.effectiveRate)}</span>
                    </td>
                    <td className='nb-commissions__table--right'>
                      {row.commissionRate != null ? (
                        <span className='nb-commissions__rate nb-commissions__rate--muted'>
                          {formatRate(row.commissionRate)}
                        </span>
                      ) : (
                        <span className='nb-commissions__rate nb-commissions__rate--dashed'>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        type='button'
                        className='nb-commissions__btn nb-commissions__btn--sm'
                        onClick={() => openPartnerOverride(row)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ITEMS TAB */}
      {tab === 'items' && (
        <>
          <div className='nb-commissions__filter-bar'>
            <div className='nb-commissions__filter'>
              <span className='nb-commissions__filter-label'>Type</span>
              <select
                className='nb-commissions__filter-select'
                value={itemType}
                onChange={(e) => setItemType(e.target.value as 'experience' | 'engagement')}
              >
                <option value='experience'>Experiences</option>
                <option value='engagement'>Engagements</option>
              </select>
            </div>
            <div className='nb-commissions__filter'>
              <span className='nb-commissions__filter-label'>Search</span>
              <input
                className='nb-commissions__filter-input'
                type='text'
                placeholder='Title…'
                value={itemsSearch}
                onChange={(e) => setItemsSearch(e.target.value)}
              />
            </div>
            <div className='nb-commissions__filter'>
              <span className='nb-commissions__filter-label'>Override</span>
              <select
                className='nb-commissions__filter-select'
                value={itemsHasOverride}
                onChange={(e) => setItemsHasOverride(e.target.value)}
              >
                <option value='all'>All items</option>
                <option value='true'>With override</option>
                <option value='false'>Without override</option>
              </select>
            </div>
            <div className='nb-commissions__filter-spacer' />
          </div>

          <div className='nb-commissions__table-wrap'>
            <table className='nb-commissions__table'>
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Type</th>
                  <th>Title</th>
                  <th>Partner</th>
                  <th className='nb-commissions__table--right' style={{ width: 140 }}>Effective</th>
                  <th className='nb-commissions__table--right' style={{ width: 140 }}>Override</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {itemRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className='nb-commissions__table__empty'>
                      No items match the current filters.
                    </td>
                  </tr>
                )}
                {itemRows.map((row) => (
                  <tr key={`${row.itemType}-${row.id}`}>
                    <td>
                      <span className={`nb-commissions__scope nb-commissions__scope--${row.itemType}`}>
                        {row.itemType}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{row.title}</td>
                    <td style={{ color: '#808080' }}>{row.partnerName ?? '—'}</td>
                    <td className='nb-commissions__table--right'>
                      <span className='nb-commissions__rate'>{formatRate(row.effectiveRate)}</span>
                    </td>
                    <td className='nb-commissions__table--right'>
                      {row.commissionRate != null ? (
                        <span className='nb-commissions__rate nb-commissions__rate--muted'>
                          {formatRate(row.commissionRate)}
                        </span>
                      ) : (
                        <span className='nb-commissions__rate nb-commissions__rate--dashed'>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        type='button'
                        className='nb-commissions__btn nb-commissions__btn--sm'
                        onClick={() => openItemOverride(row)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showGlobalModal && (
        <ChangeGlobalRateModal
          isOpen={showGlobalModal}
          currentRate={String(global?.defaultRate ?? '')}
          onClose={() => setShowGlobalModal(false)}
          onSaved={async () => {
            setShowGlobalModal(false)
            await refreshDashboard()
          }}
        />
      )}

      {overrideTarget && (
        <SetOverrideModal
          isOpen={overrideTarget != null}
          target={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onSaved={handleOverrideSaved}
        />
      )}
    </div>
  )
}
